import { useState, useEffect, useCallback } from 'react';
import { Calendar, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from '../SharedUI';

export function DailyPlans({ t, embedded = false, onOpenQueue }: { t: any; embedded?: boolean; onOpenQueue?: () => void }) {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyPlanIds, setBusyPlanIds] = useState<string[]>([]);
  const isZh = t?.langCode !== 'en';
  const activeCount = plans.length;

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/plans?status=active', { credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || (t.planLoadFailed || '计划加载失败'));
      setPlans((d.plans || [])
        .filter((p: any) => (p.source === 'xiaofeng' || p.source === 'auto') && p.status !== 'done' && p.status !== 'completed' && p.status !== 'cancelled')
        .slice(0, 8));
    } catch (err: any) {
      toast.error(err?.message || (t.planLoadFailed || '计划加载失败'));
    } finally { setLoading(false); }
  }, [t.planLoadFailed]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const deletePlan = useCallback(async (id: string) => {
    setBusyPlanIds(prev => prev.includes(id) ? prev : [...prev, id]);
    try {
      const res = await fetch(`/api/plans/${id}`, { method: 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || (t.planDeleteFailed || '计划删除失败'));
      setPlans(prev => prev.filter(p => p.id !== id));
      toast.success(t.planDeleted || '计划已删除');
    } catch (err: any) {
      toast.error(err?.message || (t.planDeleteFailed || '计划删除失败'));
    } finally {
      setBusyPlanIds(prev => prev.filter(planId => planId !== id));
    }
  }, [t.planDeleteFailed, t.planDeleted]);

  const handleQueueOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenQueue?.();
  }, [onOpenQueue]);

  const handleDeletePlan = useCallback((e: React.MouseEvent, planId: string) => {
    e.stopPropagation();
    deletePlan(planId);
  }, [deletePlan]);

  const getStepInfo = (plan: any) => {
    const steps = Array.isArray(plan.steps) ? plan.steps : [];
    const done = steps.filter((step: any) => step.status === 'done' || step.status === 'skipped').length;
    const activeStep = steps.find((step: any) => step.status === 'in_progress')
      || steps.find((step: any) => step.status === 'pending');
    const label = activeStep
      ? activeStep.status === 'in_progress'
        ? (isZh ? '吸收中' : 'Absorbing')
        : activeStep.title
      : (isZh ? '等待下一轮执行' : 'Waiting for next run');
    return { steps, done, label };
  };

  const formatUpdatedAt = (value?: string) => {
    if (!value) return '';
    const time = new Date(value);
    if (Number.isNaN(time.getTime())) return '';
    return time.toLocaleTimeString(isZh ? 'zh-CN' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <GlassCard
      className={`xf-panel ${embedded ? 'h-full' : 'cursor-pointer hover:bg-white/[0.05]'} space-y-3 rounded-2xl bg-black/20 p-5 transition-colors`}
      onClick={onOpenQueue}
    >
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[12px] font-black uppercase tracking-widest text-white/65 flex items-center gap-2">
            <Calendar size={12} className="text-celestial-saturn" />
            {embedded ? (isZh ? '小枫 学习计划' : '小枫 Learning Plans') : (isZh ? '小枫 学习流' : '小枫 Learning')}
          </span>
          {!embedded && (
            <p className="mt-1 text-[11px] text-white/30">
              {activeCount > 0
                ? (isZh ? `${activeCount} 个学习计划` : `${activeCount} learning plan${activeCount === 1 ? '' : 's'}`)
                : (isZh ? '暂无学习计划' : 'No learning plans')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {onOpenQueue && (
            <button
              onClick={handleQueueOpen}
              className="xf-button h-7 px-2 text-[10px]"
            >
              {isZh ? '队列' : 'Queue'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-white/30 text-xs py-2">{isZh ? '加载中...' : 'Loading...'}</div>
      ) : plans.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 text-xs text-white/30">
          {isZh ? '小枫还没有生成学习计划。进入自主模式并开启自动处理后，她会按最近上下文持续创建。' : '小枫 has not generated learning plans yet. In autonomous mode with auto processing enabled, she will keep creating them from recent context.'}
        </div>
      ) : (
        <div className="space-y-2">
          {plans.map((plan: any) => {
            const { steps, done, label } = getStepInfo(plan);
            const progress = steps.length > 0 ? `${done}/${steps.length}` : (isZh ? '待沉淀' : 'queued');
            const updatedAt = formatUpdatedAt(plan.updatedAt);
            return (
              <div key={plan.id} className="group rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${plan.priority === 'critical' ? 'bg-red-300' : plan.priority === 'high' ? 'bg-orange-300' : plan.priority === 'medium' ? 'bg-amber-300' : 'bg-white/25'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-xs font-bold text-white/70">{plan.title}</span>
                      <span className="shrink-0 rounded-full border border-celestial-saturn/20 bg-celestial-saturn/10 px-2 py-0.5 text-[10px] font-bold text-celestial-saturn/80">
                        {progress}
                      </span>
                    </div>
                    <div className="mt-1 flex min-w-0 items-center gap-2 text-[11px] text-white/35">
                      <span className="truncate">{label}</span>
                      {updatedAt && <span className="shrink-0 font-mono">{updatedAt}</span>}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeletePlan(e, plan.id)}
                    disabled={busyPlanIds.includes(plan.id)}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/15 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-200 group-hover:opacity-100 disabled:opacity-30"
                    title={isZh ? '移除计划' : 'Remove plan'}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}
